import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EnhancedInput } from '@/components/ui/enhanced-input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/utils';
import { Search, Loader2, Trash2, RefreshCcw, Download } from 'lucide-react';
import { db } from '@/lib/firebase';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc, getDoc, where } from 'firebase/firestore';
import type { Transaction, Customer } from '@/types/firebase';
import { useAuth } from '@/components/AuthProvider';
import { canPerformAction } from '@/lib/permissions';
import { processRefund as processNMIRefund } from '@/lib/nmi';
import { showNotification } from '@/lib/generalNotifications';
import { useNavigate } from 'react-router-dom';
import { getCompanySettings, getCustomer } from '@/lib/api';
import { isWithinInterval, startOfDay, endOfDay, isSameDay } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { exportToCSV } from '@/utils/exportUtils';
import { InlineEditableCell } from '@/components/ui/InlineEditableCell';
import { InlineEditableDropdown } from '@/components/ui/InlineEditableDropdown';
import { Badge } from '@/components/ui/badge';
import { getIdToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Interface for react-datepicker's range
type DatePickerDateRange = [Date | null, Date | null]

// Type for Firestore Timestamp
interface FirestoreTimestamp {
  toDate(): Date;
}

export default function Transactions() {
  const { user, loading, isAdmin, userRole } = useAuth();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [detailTransaction, setDetailTransaction] = useState<Transaction | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const summaryTotals = useMemo(() => {
    if (!detailTransaction) {
      return null;
    }

    const platformFeeAmount = detailTransaction.platformFeeAmount || 0;
    const salesTaxAmount = detailTransaction.salesTax || 0;
    const transactionAmount = detailTransaction.amount || 0;
    const subtotal = transactionAmount - platformFeeAmount - salesTaxAmount;

    return {
      subtotal: subtotal > 0 ? subtotal : 0,
      salesTax: salesTaxAmount,
      platformFee: platformFeeAmount,
      total: transactionAmount,
    };
  }, [detailTransaction]);
  
  // Update date range state
  const [dateRange, setDateRange] = useState<DatePickerDateRange>([null, null]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (loading) return;
      
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const transactionsRef = collection(db, 'transactions');
        const q = query(
          transactionsRef,
          where('companyId', '==', user.companyId || `company_${user.uid}`),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        
        const fetchedTransactions: Transaction[] = [];
        
        // Helper function to safely convert different date formats
        const safelyConvertDate = (dateField: unknown): Date => {
          if (!dateField) return new Date(); // Default to current date if field is missing
          
          // If it's a Firestore Timestamp with toDate method
          if (typeof dateField === 'object' && dateField !== null && 'toDate' in dateField && typeof (dateField as FirestoreTimestamp).toDate === 'function') {
            return (dateField as FirestoreTimestamp).toDate();
          }
          
          // If it's an ISO string date
          if (typeof dateField === 'string') {
            return new Date(dateField);
          }
          
          // If it's already a Date object
          if (dateField instanceof Date) {
            return dateField;
          }
          
          // Fallback to current date
          return new Date();
        };
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedTransactions.push({ 
            id: doc.id, 
            ...data,
            createdAt: safelyConvertDate(data.createdAt),
            updatedAt: safelyConvertDate(data.updatedAt)
          } as Transaction);
        });
        
        // Fetch customer info for transactions that don't have company name
        const enhancedTransactions = await Promise.all(
          fetchedTransactions.map(async (transaction) => {
            if (!transaction.customerCompanyName && transaction.customerId) {
              try {
                const customerDoc = await getDoc(doc(db, 'customers', transaction.customerId));
                if (customerDoc.exists()) {
                  const customerData = customerDoc.data() as Customer;
                  return {
                    ...transaction,
                    customerCompanyName: customerData.companyName || undefined
                  };
                }
              } catch (err) {
                console.error('Error fetching customer for transaction:', err);
              }
            }
            return transaction;
          })
        );
        
        console.log('Fetched transactions:', enhancedTransactions);
        setTransactions(enhancedTransactions);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading transactions:', error);
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [user, loading]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Corrected date filtering logic with explicit single-day check and logging
  const filterTransactionsByDate = (transactions: Transaction[]) => {
    const [startDate, endDate] = dateRange;
    // If no start date, no filtering needed based on date
    if (!startDate) return transactions;

    // console.log(`Filtering with range: Start=${startDate?.toISOString()}, End=${endDate?.toISOString()}`);

    return transactions.filter(transaction => {
      const transactionDate = new Date(transaction.createdAt);
      let match = false;

      // Log the date being checked
      // console.log(`Checking Transaction ID: ${transaction.id}, Date: ${transactionDate.toISOString()}`);

      // Case 1: Start and End dates are selected
      if (startDate && endDate) {
        // Check if the selected range is a single day
        if (isSameDay(startDate, endDate)) {
          // Compare year, month, and day directly
          match = (
            transactionDate.getFullYear() === startDate.getFullYear() &&
            transactionDate.getMonth() === startDate.getMonth() &&
            transactionDate.getDate() === startDate.getDate()
          );
          // console.log(`  Single day check (isSameDay=true). StartDate=${startDate.toISOString()}, TransactionDate=${transactionDate.toISOString()}, Match=${match}`);
        } else {
          // Handle multi-day range: use start of the start day and end of the end day
          const rangeStart = startOfDay(startDate);
          const rangeEnd = endOfDay(endDate);
          match = isWithinInterval(transactionDate, { start: rangeStart, end: rangeEnd });
          // console.log(`  Multi-day check. RangeStart=${rangeStart.toISOString()}, RangeEnd=${rangeEnd.toISOString()}, TransactionDate=${transactionDate.toISOString()}, Match=${match}`);
        }
      }
      // Case 2: Only start date is selected (treat as single day filter)
      else if (startDate && !endDate) {
         // Compare year, month, and day directly
         match = (
            transactionDate.getFullYear() === startDate.getFullYear() &&
            transactionDate.getMonth() === startDate.getMonth() &&
            transactionDate.getDate() === startDate.getDate()
          );
          // console.log(`  Single day check (only start date). StartDate=${startDate.toISOString()}, TransactionDate=${transactionDate.toISOString()}, Match=${match}`);
      }
      
      return match;
    });
  };

  // Update filteredTransactions to include date filter
  const filteredTransactions = filterTransactionsByDate(transactions.filter(transaction => {
    const searchLower = searchTerm.toLowerCase();
    
    // Format amount to enable searching without decimal separator differences
    const amountString = transaction.amount.toString();
    const amountFormatted = formatCurrency(transaction.amount).toLowerCase();
    // Remove all non-numeric characters for raw number search
    const amountRaw = amountString.replace(/[^0-9]/g, '');
    // Create normalized search terms with both . and , for comparison
    const searchNormalized = searchLower.replace(/[,.]/g, '');
    
    const matchesSearch = (
      (transaction.transactionId?.toLowerCase().includes(searchLower) || false) ||
      (transaction.customerName?.toLowerCase().includes(searchLower) || false) ||
      (transaction.description?.toLowerCase().includes(searchLower) || false) ||
      // Add amount search options
      amountFormatted.includes(searchLower) ||
      amountString.includes(searchLower) ||
      // Check for numeric search without separators
      (searchNormalized.length > 0 && /^\d+$/.test(searchNormalized) && amountRaw.includes(searchNormalized))
    );
    
    // Apply status filter if set
    const matchesStatus = statusFilter ? transaction.status === statusFilter : true;
    
    // Apply payment type filter if set
    const matchesPaymentType = paymentTypeFilter ? 
      (paymentTypeFilter === 'credit_card' && transaction.paymentMethod === 'credit_card') ||
      (paymentTypeFilter === 'bank_account' && transaction.paymentMethod === 'ach') : 
      true;
    
    return matchesSearch && matchesStatus && matchesPaymentType;
  }));

  // Sort filtered transactions
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let comparison = 0;
    
    if (sortBy === 'date') {
      comparison = a.createdAt.getTime() - b.createdAt.getTime();
    } else if (sortBy === 'amount') {
      comparison = a.amount - b.amount;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedTransactions.length / pageSize);
  const paginatedTransactions = sortedTransactions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to first page when date range, search term, filters, or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, searchTerm, pageSize, statusFilter, paymentTypeFilter, sortBy, sortOrder]);

  const handleRefund = async (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsRefundDialogOpen(true);
  };

  const processRefund = async () => {
    if (!selectedTransaction) return;

    try {
      setIsProcessing(true);
      console.log('Starting refund process for transaction:', selectedTransaction);

      // Process refund through NMI
      const response = await processNMIRefund(selectedTransaction.transactionId);
      console.log('NMI Refund response:', response);

      if (response.success) {
        const now = new Date();
        // Update transaction status in Firebase
        const transactionRef = doc(db, 'transactions', selectedTransaction.id);
        const updateData: Partial<Transaction> = {
          status: 'refunded' as const,
          updatedAt: now
        };
        
        console.log('Updating Firebase with data:', updateData);
        console.log('Transaction reference:', transactionRef.path);
        
        try {
          await updateDoc(transactionRef, updateData);
          console.log('Firebase update successful');
          
          // Verify the update
          const updatedDoc = await getDoc(transactionRef);
          console.log('Updated document data:', updatedDoc.data());

          // Update local state
          setTransactions(prevTransactions =>
            prevTransactions.map(t =>
              t.id === selectedTransaction.id
                ? { ...t, ...updateData }
                : t
            )
          );

          // Send refund email notification if enabled
          try {
            // Get customer details to ensure we have the email
            const customer = await getCustomer(selectedTransaction.customerId);
            
            // Get company settings to check if refund emails are enabled
            const companySettings = await getCompanySettings(selectedTransaction.companyId);
            console.log('Company settings for refund email:', companySettings?.emailSettings?.enableRefundEmail);
            
            // If refund emails are enabled and we have the required email settings
            if (companySettings?.emailSettings?.enableRefundEmail && 
                companySettings.emailSettings.sendgridApiKey && 
                companySettings.emailSettings.fromEmail && 
                companySettings.emailSettings.refundIssued) {
              
              console.log('Refund email is ENABLED and all required settings present');
              
              // Use the same email API endpoint as regular invoice emails
              const origin = typeof window !== 'undefined' ? window.location.origin : process.env.NEXT_PUBLIC_APP_URL;
              const apiUrl = new URL('/api/email', origin).toString();
              
              // Prepare the template with refund-specific data
              let emailTemplate = companySettings.emailSettings.refundIssued;
              
              // Get invoice information if available
              let invoiceNumber = 'N/A';
              if (selectedTransaction.invoiceId) {
                const invoiceRef = doc(db, 'invoices', selectedTransaction.invoiceId);
                const invoiceDoc = await getDoc(invoiceRef);
                if (invoiceDoc.exists()) {
                  const invoiceData = invoiceDoc.data();
                  invoiceNumber = invoiceData.invoiceNumber;
                }
              }
              
              // Replace template variables with actual data
              const templateData = {
                customerName: customer.firstName + ' ' + customer.lastName,
                invoiceNumber: invoiceNumber,
                refundAmount: formatCurrency(selectedTransaction.amount),
                refundDate: now.toLocaleDateString(),
                companyName: companySettings.name || ''
              };
              
              // Replace all template variables
              Object.entries(templateData).forEach(([key, value]) => {
                const regex = new RegExp(`{{${key}}}`, 'g');
                emailTemplate = emailTemplate.replace(regex, value);
              });
              
              // Construct a pseudo-invoice object for the email API
              const pseudoInvoice = {
                id: selectedTransaction.invoiceId || selectedTransaction.id,
                customerEmail: customer.email,
                companyId: selectedTransaction.companyId,
                customerName: customer.firstName + ' ' + customer.lastName
              };
              
              // Send email using the same API endpoint as regular invoice emails
              const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  invoice: pseudoInvoice,
                  template: emailTemplate,
                  subject: companySettings.emailSettings.refundIssuedSubject || 'Refund Processed',
                  isRefundEmail: true // Flag to indicate this is a refund email
                }),
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                console.error('Failed to send refund email:', errorText);
              } else {
                console.log('Refund email sent successfully');
              }
            } else {
              console.log('Refund emails are disabled or missing required settings');
            }
          } catch (emailError) {
            console.error('Error sending refund email:', emailError);
            // Continue with transaction update even if email fails
          }

          showNotification({
            title: "Success",
            message: `Refund of ${formatCurrency(selectedTransaction.amount)} has been processed successfully.`,
            type: "success"
          });
        } catch (firebaseError) {
          console.error('Firebase update error:', firebaseError);
          throw firebaseError;
        }
      } else {
        throw new Error('NMI refund failed');
      }
    } catch (error) {
      console.error('Refund error:', error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to process refund",
        type: "error"
      });
    } finally {
      setIsProcessing(false);
      setIsRefundDialogOpen(false);
      setSelectedTransaction(null);
    }
  };

  const handleDeleteClick = (transaction: Transaction) => {
    setTransactionToDelete(transaction);
    setIsDeleteDialogOpen(true);
  };

  const handleRowClick = (transaction: Transaction) => {
    setDetailTransaction(transaction);
    setDetailDialogOpen(true);
  };

  const closeDetailDialog = () => {
    setDetailDialogOpen(false);
    setDetailTransaction(null);
  };

  const deleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      setIsProcessing(true);
      await deleteDoc(doc(db, 'transactions', transactionToDelete.id));
      
      // Update local state
      setTransactions(prevTransactions => 
        prevTransactions.filter(t => t.id !== transactionToDelete.id)
      );

      showNotification({
        title: "Success",
        message: "The transaction has been successfully deleted.",
        type: "success"
      });
    } catch (error) {
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete transaction",
        type: "error"
      });
    } finally {
      setIsProcessing(false);
      setIsDeleteDialogOpen(false);
      setTransactionToDelete(null);
    }
  };

  const handleFieldUpdate = async (transactionId: string, field: 'amount' | 'platformFeeAmount' | 'status', value: string | number) => {
    try {
      const token = await getIdToken(auth.currentUser!);
      const response = await fetch(`/api/transactions/${transactionId}/update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ field, value }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update transaction');
      }

      // Update local state
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.id === transactionId ? { ...t, [field]: value } : t
        )
      );

      showNotification({
        title: "Success",
        message: `Transaction ${field} updated successfully`,
        type: "success"
      });
    } catch (error) {
      console.error('Error updating transaction:', error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update transaction",
        type: "error"
      });
      throw error;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-600 text-white';
      case 'failed':
        return 'bg-red-600 text-white';
      case 'refunded':
        return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
      case 'overdue':
        return 'bg-red-600 text-white';
      case 'pending':
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20';
      case 'completed':
        return 'bg-green-50 text-green-700 ring-1 ring-green-600/20';
      case 'cancelled':
        return 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20';
    }
  };

  // Add CSV export function
  const handleExportCSV = () => {
    // Define column structure for CSV with correct type
    const columns = [
      { key: 'createdAt' as keyof Transaction, header: 'Date' },
      { key: 'customerName' as keyof Transaction, header: 'Customer Name' },
      { key: 'customerCompanyName' as keyof Transaction, header: 'Customer Company' },
      { key: 'invoiceNumber' as keyof Transaction, header: 'Invoice #' },
      { key: 'description' as keyof Transaction, header: 'Description' },
      { key: 'amount' as keyof Transaction, header: 'Amount' },
      { key: 'platformFeeAmount' as keyof Transaction, header: 'Platform Fee' },
      { key: 'status' as keyof Transaction, header: 'Status' },
      { key: 'paymentMethod' as keyof Transaction, header: 'Payment Method' },
      { key: 'transactionId' as keyof Transaction, header: 'Transaction ID' },
    ];

    // Format data for CSV (ensure dates are properly formatted)
    const formattedData = filteredTransactions.map(t => ({
      ...t,
      createdAt: new Date(t.createdAt).toLocaleDateString(),
      amount: formatCurrency(t.amount),
      platformFeeAmount: t.platformFeeAmount ? formatCurrency(t.platformFeeAmount) : 'N/A'
    }));

    // Export using utility function
    exportToCSV(formattedData, 'Transactions', columns);
  };

  return (
    <div className="max-w-[90rem] mx-auto py-8 space-y-8">
      <div className="flex flex-col space-y-4">

        {/* Modern Search and Filter Bar */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <EnhancedInput
                placeholder="Search transactions by customer, description, or amount..."
                value={searchTerm}
                onChange={handleSearch}
                leftIcon={<Search className="h-4 w-4" />}
                className="w-full"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {/* Date Range Picker */}
              <DateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
                align="end"
                className="w-[240px]"
              />
              
              {/* Status Filter */}
              <Select value={statusFilter || 'all'} onValueChange={(value) => setStatusFilter(value === 'all' ? null : value)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Payment Type Filter */}
              <Select value={paymentTypeFilter || 'all'} onValueChange={(value) => setPaymentTypeFilter(value === 'all' ? null : value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Payment Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payment Types</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="bank_account">Bank Account</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Export Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={handleExportCSV}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Export to CSV</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </Card>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-md">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100" onClick={() => { setSortBy('date'); setSortOrder(sortBy === 'date' && sortOrder === 'desc' ? 'asc' : 'desc'); }}>
                <div className="flex items-center">Date{sortBy === 'date' && (<span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}</div>
              </th>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-left">Customer</th>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-left">Description</th>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-right cursor-pointer hover:bg-gray-100" onClick={() => { setSortBy('amount'); setSortOrder(sortBy === 'amount' && sortOrder === 'desc' ? 'asc' : 'desc'); }}>
                <div className="flex items-center justify-end">Amount{sortBy === 'amount' && (<span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>)}</div>
              </th>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-center">
                <div className="flex flex-col items-center"><span>Platform</span><span>Fee</span></div>
              </th>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-center w-[90px]">Status</th>
              <th className="py-3 px-4 text-xs font-bold text-gray-700 uppercase tracking-wider text-center">Payment Type</th>
              <th className="py-3 px-2 text-xs font-bold text-gray-700 uppercase tracking-wider text-center w-[90px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-gray-400">
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-10 w-10 mb-2 animate-spin text-gray-300" />
                    <span className="text-lg font-medium">Loading transactions...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedTransactions.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-gray-400">
                  <div className="flex flex-col items-center">
                    <Search className="h-10 w-10 mb-2 text-gray-300" />
                    <span className="text-lg font-medium">No transactions found matching your filters</span>
                    <Button variant="link" className="mt-2" onClick={() => { 
                      setSearchTerm(''); 
                      setStatusFilter(null); 
                      setPaymentTypeFilter(null);
                      setDateRange([null, null]);
                    }}>
                      Clear all filters
                    </Button>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedTransactions.map((transaction) => (
              <tr
                key={transaction.id}
                className="hover:bg-blue-50/40 transition cursor-pointer"
                onClick={() => handleRowClick(transaction)}
              >
                  <td className="py-3 px-4 text-sm align-middle">
                    <div className="text-gray-900">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {new Date(transaction.createdAt).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm align-middle">
                    {transaction.customerCompanyName ? (
                      <div>
                        <div className="font-medium">
                          {transaction.customerCompanyName}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {transaction.customerName}
                        </div>
                      </div>
                    ) : (
                      <div className="font-medium">
                        {transaction.customerName}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm align-middle" onClick={(e) => e.stopPropagation()}>
                    {transaction.invoiceId ? (
                      <span 
                        className="underline text-blue-600 cursor-pointer hover:text-blue-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (transaction.invoiceId) {
                            navigate(`/app/invoices/edit/${transaction.invoiceId}`);
                          }
                        }}
                      >
                        {transaction.description}
                      </span>
                    ) : (
                      transaction.description
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-right align-middle" onClick={(e) => e.stopPropagation()}>
                    {user?.role === 'super_admin' ? (
                      <InlineEditableCell
                        value={transaction.amount}
                        onSave={(newValue) => handleFieldUpdate(transaction.id, 'amount', newValue)}
                        type="currency"
                        formatDisplay={(value) => formatCurrency(value as number)}
                      />
                    ) : (
                      <div className="text-gray-900">{formatCurrency(transaction.amount)}</div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-center align-middle" onClick={(e) => e.stopPropagation()}>
                    {user?.role === 'super_admin' ? (
                      transaction.platformFeeAmount > 0 ? (
                        <InlineEditableCell
                          value={transaction.platformFeeAmount}
                          onSave={(newValue) => handleFieldUpdate(transaction.id, 'platformFeeAmount', newValue)}
                          type="currency"
                          formatDisplay={(value) => formatCurrency(value as number)}
                        />
                      ) : (
                        <InlineEditableCell
                          value={0}
                          onSave={(newValue) => handleFieldUpdate(transaction.id, 'platformFeeAmount', newValue)}
                          type="currency"
                          formatDisplay={() => 'N/A'}
                        />
                      )
                    ) : (
                      <div className="text-gray-900">
                        {transaction.platformFeeAmount > 0 
                          ? formatCurrency(transaction.platformFeeAmount)
                          : <span className="text-xs text-gray-400">N/A</span>
                        }
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center align-middle w-[90px]" onClick={(e) => e.stopPropagation()}>
                    {user?.role === 'super_admin' ? (
                        <InlineEditableDropdown
                          value={transaction.status}
                          options={[
                            { value: 'pending', label: 'PENDING' },
                            { value: 'completed', label: 'COMPLETED' },
                            { value: 'paid', label: 'PAID' },
                            { value: 'failed', label: 'FAILED' },
                            { value: 'refunded', label: 'REFUNDED' },
                            { value: 'cancelled', label: 'CANCELLED' },
                          ]}
                          onSave={(newValue) => handleFieldUpdate(transaction.id, 'status', newValue)}
                          renderValue={(value) => (
                            <Badge className={`px-2.5 py-1 text-[0.75rem] font-semibold min-w-[64px] flex items-center justify-center rounded-none leading-4 ${getStatusBadgeClass(value)}`}>
                              {value === 'overdue' ? 'PAST DUE' : value.charAt(0).toUpperCase() + value.slice(1)}
                            </Badge>
                          )}
                        />
                      ) : (
                        <Badge className={`px-2.5 py-1 text-[0.75rem] font-semibold min-w-[64px] flex items-center justify-center rounded-none leading-4 ${getStatusBadgeClass(transaction.status)}`}>
                          {transaction.status === 'overdue' ? 'PAST DUE' : transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </Badge>
                      )}
                  </td>
                  <td className="py-3 px-4 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                    <Badge variant="outline" className="bg-gray-50 text-gray-700 border-0">
                      {transaction.paymentMethod === 'credit_card' 
                        ? (transaction.vaultType === 'existing' ? 'Saved Card' : 'Credit Card') : 
                       transaction.paymentMethod === 'ach' ? 'Bank Account' : 
                       transaction.paymentMethod || 'Unknown'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-center gap-1">
                      {isAdmin && transaction.status === 'paid' && canPerformAction(user, 'transactions', 'canEdit', userRole) && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 p-1"
                                onClick={() => handleRefund(transaction)}
                              >
                                <RefreshCcw className="h-3.5 w-3.5 text-red-600 hover:text-red-700 transition-colors" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Refund transaction</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {transaction.status !== 'paid' && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 p-1"
                                onClick={() => handleDeleteClick(transaction)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-red-500 hover:text-red-700 transition-colors" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete transaction</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
            </tbody>
          </table>
        </div>

      <Dialog open={detailDialogOpen} onOpenChange={(open) => (open ? setDetailDialogOpen(true) : closeDetailDialog())}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>Snapshot of the data captured for this transaction.</DialogDescription>
          </DialogHeader>

          {detailTransaction && (
            <div className="space-y-6 text-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Customer</p>
                  <p className="font-medium text-foreground">
                    {detailTransaction.customerCompanyName || detailTransaction.customerName || '—'}
                  </p>
                  {detailTransaction.customerCompanyName && (
                    <p className="text-xs text-muted-foreground mt-0.5">{detailTransaction.customerName}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Status</p>
                  <Badge className={`mt-1 px-2.5 py-1 text-[0.75rem] font-semibold rounded-none leading-4 ${getStatusBadgeClass(detailTransaction.status)}`}>
                    {detailTransaction.status === 'overdue'
                      ? 'Past Due'
                      : detailTransaction.status.charAt(0).toUpperCase() + detailTransaction.status.slice(1)}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Payment Method</p>
                  <p className="font-medium text-foreground">
                    {detailTransaction.paymentMethod === 'credit_card'
                      ? `Credit Card ${detailTransaction.cardLast4 ? `ending in ${detailTransaction.cardLast4}` : ''}${detailTransaction.cardType ? ` (${detailTransaction.cardType})` : ''}`
                      : detailTransaction.paymentMethod === 'ach'
                      ? `Bank Account ${detailTransaction.cardLast4 ? `ending in ${detailTransaction.cardLast4}` : ''}`
                      : detailTransaction.paymentMethod || '—'}
                  </p>
                  {detailTransaction.vaultType && (
                    <p className="text-xs text-muted-foreground mt-0.5">Stored method: {detailTransaction.vaultType}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Transaction ID</p>
                  <p className="text-foreground break-all">{detailTransaction.transactionId || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Invoice</p>
                  <p className="text-foreground">{detailTransaction.invoiceId || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Description</p>
                  <p className="text-foreground">{detailTransaction.description || '—'}</p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Created At</p>
                  <p className="text-foreground">{new Date(detailTransaction.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Updated At</p>
                  <p className="text-foreground">
                    {detailTransaction.updatedAt ? new Date(detailTransaction.updatedAt).toLocaleString() : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Failure Reason</p>
                  <p className="text-foreground">{detailTransaction.failureReason || '—'}</p>
                </div>
              </div>

              {detailTransaction.metadata && Object.keys(detailTransaction.metadata).length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase text-muted-foreground">Gateway Metadata</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {Object.entries(detailTransaction.metadata).map(([key, value]) => (
                      <div key={key}>
                        <p className="font-medium text-foreground">{key}</p>
                        <p className="text-muted-foreground break-all">{value ? String(value) : '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {summaryTotals && (
                <div className="flex justify-end">
                  <div className="w-full sm:w-auto rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
                    <dl className="grid gap-3 text-sm">
                      <div className="flex justify-between gap-10">
                        <dt className="text-muted-foreground">Subtotal</dt>
                        <dd className="font-medium">{formatCurrency(summaryTotals.subtotal)}</dd>
                      </div>
                      <div className="flex justify-between gap-10">
                        <dt className="text-muted-foreground">Taxes</dt>
                        <dd className="font-medium">{formatCurrency(summaryTotals.salesTax)}</dd>
                      </div>
                      <div className="flex justify-between gap-10">
                        <dt className="text-muted-foreground">Platform Fees</dt>
                        <dd className="font-medium">{formatCurrency(summaryTotals.platformFee)}</dd>
                      </div>
                      <div className="flex justify-between gap-10 border-t pt-2 font-semibold">
                        <dt>Total Collected</dt>
                        <dd>{formatCurrency(summaryTotals.total)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={closeDetailDialog}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {filteredTransactions.length > 0 && (
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-4 px-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              Showing {Math.min(filteredTransactions.length, (currentPage - 1) * pageSize + 1)}-
              {Math.min(filteredTransactions.length, currentPage * pageSize)} of {filteredTransactions.length} transactions
            </span>
            <Select value={pageSize.toString()} onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(1);
            }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Page size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-1 mx-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  // If 5 or fewer pages, show all page numbers
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  // If on page 1-3, show pages 1-5
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // If on last 3 pages, show last 5 pages
                  pageNum = totalPages - 4 + i;
                } else {
                  // Otherwise show current page and 2 pages before/after
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    className="w-8 h-8 p-0"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--accent))]">Delete Transaction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transaction? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {transactionToDelete && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Transaction ID:</span>
                  <span className="font-medium">Declined Transaction</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-medium">{formatCurrency(transactionToDelete.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-medium">{transactionToDelete.customerName}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteTransaction}
              disabled={isProcessing}
              variant="destructive"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Delete Transaction'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--accent))]">Process Refund</DialogTitle>
            <DialogDescription>
              Please confirm that you want to process a refund for this transaction.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedTransaction && (
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Transaction ID:</span>
                  <span className="font-medium">{selectedTransaction.id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Original Amount:</span>
                  <span className="font-medium text-[hsl(var(--accent))]">{formatCurrency(selectedTransaction.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-medium">{selectedTransaction.customerName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Date:</span>
                  <span className="font-medium">
                    {new Date(selectedTransaction.createdAt).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Refund Amount:</span>
                    <span className="font-bold text-[hsl(var(--accent))]">{formatCurrency(selectedTransaction.amount)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Note: The full transaction amount will be refunded.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsRefundDialogOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={processRefund}
              disabled={isProcessing}
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))] hover:opacity-90"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm Refund'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
